import { z } from 'zod';
import { cursorPageQuerySchema } from './pagination.js';
export const NOTIFICATION_CATEGORIES = [
    'TASK_RESULTS',
    'BILLING',
    'SECURITY',
    'REMINDERS',
    'SUMMARIES',
    'TIPS',
    'ANNOUNCEMENTS',
];
/**
 * Every event the notification registry can emit. Owned here rather than in
 * the registry so the console's type filter and the registry are one list:
 * `notification-registry.ts` imports it, and its startup check fails when a
 * definition is missing for any entry.
 */
export const NOTIFICATION_EVENT_TYPES = [
    'plan.ready',
    'plan.failed',
    // A Premium meal plan's run (src/modules/meal-plans): never `plan.*`.
    'meal_plan.ready',
    'meal_plan.failed',
    'food_scan.ready',
    'food_scan.failed',
    'billing.issue',
    'billing.access_will_end',
    'billing.expired',
    'security.session_reuse',
    'security.new_device_login',
    'workout.reminder',
    'meal.reminder',
    'coaching.tip',
    'weekly.summary',
    'streak.at_risk',
    'monthly.recap',
    // Rhythm's two families (phase 4): a day still open, and someone who has stopped coming.
    'rhythm.day_open',
    'rhythm.winback',
    'admin.announcement',
    // Steado for Gyms: the member's pushes (member frame N1), from the gym's decisions.
    'gym.member_approved',
    'gym.member_declined',
    'gym.plan_published',
    'gym.trainer_changed',
    'gym.coaching_ended',
    'gym.trainer_message',
    'gym.request_answered',
    'gym.workout_logged',
    // A watch's workouts reaching Steado through the phone's health store (activities).
    'activity.synced',
];
export const NOTIFICATION_PRIORITIES = ['P0', 'P1', 'P2', 'P3'];
export const NOTIFICATION_CHANNELS = ['PUSH'];
export const NOTIFICATION_DELIVERY_STATUSES = [
    'SCHEDULED',
    'PROCESSING',
    'SENT',
    'FAILED_RETRYABLE',
    'FAILED_TERMINAL',
    'DEFERRED',
    'SUPPRESSED',
];
export const NOTIFICATION_ATTEMPT_STATUSES = [
    'STARTED',
    'ACCEPTED',
    'RETRYABLE_FAILURE',
    'TERMINAL_FAILURE',
];
export const NOTIFICATION_OUTBOX_STATUSES = ['PENDING', 'CLAIMED', 'PROCESSED', 'FAILED'];
export const NOTIFICATION_SUPPRESSION_REASONS = [
    'DELIVERY_DISABLED',
    'PUSH_DISABLED',
    'RECORD_ONLY',
    'KILL_SWITCH',
    'USER_DELETED',
    'INSTALLATION_INACTIVE',
    'PREFERENCE_DISABLED',
    'NOTIFICATION_CANCELLED',
    'NOTIFICATION_EXPIRED',
    'QUIET_HOURS_TTL',
    'SOURCE_IRRELEVANT',
    'INVALID_TOKEN',
    'ATTEMPTS_EXHAUSTED',
    'ROLLOUT_EXCLUDED',
    'EVENT_DISABLED',
    'FREQUENCY_CAP',
    'APP_TOO_OLD',
    'CONSENT_MISSING',
    'USER_DEACTIVATED',
    'INVALID_INSTALLATION',
    'CATEGORY_DISABLED',
    'REMINDER_SUPERSEDED',
    'REST_DAY',
    'WORKOUT_COMPLETED',
    'WORKOUT_OPEN',
    'MEAL_LOGGED',
    'PLAN_STALE',
    'PLAN_SUPERSEDED',
    'PLAN_CANCELLED',
    'OPTIONAL_DAILY_CAP',
    'OPTIONAL_WEEKLY_CAP',
    'OPTIONAL_MIN_INTERVAL',
    'QUIET_HOURS_DEFERRED',
    'OPERATOR_SUPPRESSED',
    'RUNTIME_KILL_SWITCH',
    'STREAK_SAFE',
    'STREAK_WEEKLY_CAP',
    /** Historical: no longer emitted. Rows written before the weekly review still carry it. */
    'NO_PLANNED_WORKOUTS',
    'USER_INACTIVE',
    /** A weekly review with nothing measured in it — the one week the summary stays silent. */
    'NO_MEASURED_FACTS',
    /** Rhythm: the user's weekly budget of optional pushes for their state is spent. */
    'RHYTHM_BUDGET',
    /** Rhythm: the user's state does not get this family. */
    'RHYTHM_STATE',
    /** Rhythm: the day the nudge was about has something logged on it. */
    'RHYTHM_DAY_KEPT',
    /** Rhythm: the user came back before a win-back step went out. */
    'RHYTHM_RETURNED',
];
export const NOTIFICATION_OPERATOR_REASONS = [
    'INCIDENT_RESPONSE',
    'PROVIDER_RECOVERED',
    'CONFIGURATION_CORRECTED',
    'ROLLOUT_STOP',
];
export const adminNotificationDeliveryParamsSchema = z.object({ deliveryId: z.string().uuid() });
export const adminNotificationAttemptSchema = z.object({
    id: z.string().uuid(),
    attemptNumber: z.number().int().positive(),
    status: z.enum(NOTIFICATION_ATTEMPT_STATUSES),
    provider: z.string().max(32),
    providerMessageId: z.string().nullable(),
    errorClass: z.string().max(32).nullable(),
    errorCode: z.string().max(64).nullable(),
    startedAt: z.string(),
    finishedAt: z.string().nullable(),
});
export const adminNotificationDeliverySchema = z.object({
    id: z.string().uuid(),
    notificationId: z.string().uuid(),
    outboxId: z.string().uuid().nullable(),
    userId: z.string().uuid(),
    installationId: z.string().uuid(),
    eventType: z.string().max(64),
    eventVersion: z.number().int().positive(),
    category: z.enum(NOTIFICATION_CATEGORIES),
    priority: z.enum(NOTIFICATION_PRIORITIES),
    channel: z.enum(NOTIFICATION_CHANNELS),
    status: z.enum(NOTIFICATION_DELIVERY_STATUSES),
    dueAt: z.string(),
    expiresAt: z.string(),
    attemptCount: z.number().int().nonnegative(),
    maxAttempts: z.number().int().positive(),
    processingAt: z.string().nullable(),
    lastAttemptAt: z.string().nullable(),
    sentAt: z.string().nullable(),
    providerMessageId: z.string().nullable(),
    lastErrorClass: z.string().max(32).nullable(),
    lastErrorCode: z.string().max(64).nullable(),
    suppressionReason: z.enum(NOTIFICATION_SUPPRESSION_REASONS).nullable(),
    notificationCancelledAt: z.string().nullable(),
    installation: z.object({
        platform: z.enum(['ANDROID', 'IOS', 'WEB']),
        permission: z.enum(['UNKNOWN', 'GRANTED', 'DENIED']),
        active: z.boolean(),
        invalidatedAt: z.string().nullable(),
        invalidationReason: z.string().max(64).nullable(),
    }),
    attempts: z.array(adminNotificationAttemptSchema),
    retryable: z.boolean(),
    retryBlockedReason: z.string().nullable(),
    suppressible: z.boolean(),
    suppressBlockedReason: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
/**
 * One person's recent deliveries.
 *
 * A trimmed delivery: the per-attempt history, the installation block and the
 * error detail stay on the by-id route. The list answers "did anything reach
 * them, and when", and a page of twenty full deliveries is a page of mostly
 * nulls. Same paging as every other user tab, keyed on (dueAt, id).
 */
export const adminUserNotificationSchema = z.object({
    id: z.string().uuid(),
    eventType: z.string().max(64),
    category: z.enum(NOTIFICATION_CATEGORIES),
    channel: z.enum(NOTIFICATION_CHANNELS),
    status: z.enum(NOTIFICATION_DELIVERY_STATUSES),
    dueAt: z.string(),
    sentAt: z.string().nullable(),
    attemptCount: z.number().int().nonnegative(),
    /** Why it never went. Null on anything that was not held back. */
    suppressionReason: z.enum(NOTIFICATION_SUPPRESSION_REASONS).nullable(),
});
export const adminUserNotificationsQuerySchema = cursorPageQuerySchema;
/**
 * A row of the all-deliveries list: the user tab's trimmed delivery plus who
 * it went to and on what device.
 *
 * Name and phone ride on the row for the same reason a food request carries
 * them: a list of user uuids makes the operator open every row to learn
 * whether one person got six of them. Still no notification copy, route or
 * token material.
 */
export const adminNotificationDeliveryListItemSchema = adminUserNotificationSchema.extend({
    priority: z.enum(NOTIFICATION_PRIORITIES),
    platform: z.enum(['ANDROID', 'IOS', 'WEB']),
    lastErrorCode: z.string().max(64).nullable(),
    userId: z.string().uuid(),
    displayName: z.string().nullable(),
    phone: z.string(),
});
export const NOTIFICATION_DELIVERY_TIMINGS = ['past', 'upcoming'];
/**
 * Filters for the all-deliveries list. `search` is one box for whatever the
 * operator has: part of a name or phone number, or a user, notification or
 * delivery uuid pasted from a log line.
 *
 * `when` splits the list at now: `past` is due already, newest first;
 * `upcoming` is not due yet, soonest first. Reminders are scheduled days
 * ahead, so an unsplit list opens on a wall of tomorrow's reminders. Absent,
 * the list is everything, newest due first.
 */
export const adminNotificationDeliveryListQuerySchema = cursorPageQuerySchema.extend({
    when: z.enum(NOTIFICATION_DELIVERY_TIMINGS).optional(),
    status: z.enum(NOTIFICATION_DELIVERY_STATUSES).optional(),
    category: z.enum(NOTIFICATION_CATEGORIES).optional(),
    eventType: z.enum(NOTIFICATION_EVENT_TYPES).optional(),
    search: z.string().trim().max(120).optional(),
});
export const adminNotificationMetricsQuerySchema = z.object({
    windowMinutes: z.coerce.number().int().min(5).max(10_080).default(1_440),
});
const sloMeasurementSchema = z.object({
    sampleSize: z.number().int().nonnegative(),
    value: z.number().nullable(),
    target: z.number(),
    comparison: z.enum(['LTE', 'GTE']),
    healthy: z.boolean().nullable(),
});
export const adminNotificationMetricsSchema = z.object({
    generatedAt: z.string(),
    windowMinutes: z.number().int().positive(),
    queryMs: z.number().int().nonnegative(),
    backlog: z.object({
        outboxCount: z.number().int().nonnegative(),
        oldestOutboxAgeSeconds: z.number().nonnegative().nullable(),
        dueDeliveryCount: z.number().int().nonnegative(),
        oldestDueDeliveryAgeSeconds: z.number().nonnegative().nullable(),
        p0p1DueDeliveryCount: z.number().int().nonnegative(),
        oldestP0P1DueDeliveryAgeSeconds: z.number().nonnegative().nullable(),
    }),
    slo: z.object({
        transactionalOutboxLag: sloMeasurementSchema,
        reminderSchedulerLag: sloMeasurementSchema,
        providerAcceptance: sloMeasurementSchema,
    }),
    deliveryOutcomes: z.array(z.object({ status: z.enum(NOTIFICATION_DELIVERY_STATUSES), count: z.number().int().nonnegative() })),
    attemptOutcomes: z.array(z.object({
        status: z.enum(NOTIFICATION_ATTEMPT_STATUSES),
        errorClass: z.enum(['RETRYABLE', 'TERMINAL']).nullable(),
        errorCode: z
            .enum([
            'INVALID_TOKEN',
            'QUOTA_EXCEEDED',
            'PROVIDER_UNAVAILABLE',
            'PROVIDER_TIMEOUT',
            'PROVIDER_REJECTED',
            'PROVIDER_OTHER',
        ])
            .nullable(),
        count: z.number().int().nonnegative(),
    })),
    suppressions: z.array(z.object({
        reason: z.enum(NOTIFICATION_SUPPRESSION_REASONS),
        count: z.number().int().nonnegative(),
    })),
    /**
     * Deliveries whose state changed in the window, per event type and grouped
     * by how they ended — the same window and clock as `deliveryOutcomes`.
     * Busiest first.
     */
    eventTypes: z.array(z.object({
        eventType: z.string().max(64),
        sent: z.number().int().nonnegative(),
        suppressed: z.number().int().nonnegative(),
        failed: z.number().int().nonnegative(),
        pending: z.number().int().nonnegative(),
    })),
    dedupeHits: z.number().int().nonnegative(),
    invalidTokens: z.object({
        rejected: z.number().int().nonnegative(),
        cleanupBreaches: z.number().int().nonnegative(),
    }),
});
export const adminNotificationMutationSchema = z.object({
    confirmation: z.string().uuid(),
    reason: z.enum(NOTIFICATION_OPERATOR_REASONS),
});
export const adminNotificationMutationResultSchema = z.object({
    deliveryId: z.string().uuid(),
    status: z.enum(NOTIFICATION_DELIVERY_STATUSES),
    dueAt: z.string(),
});
export const adminNotificationControlSchema = z.object({
    key: z
        .string()
        .regex(/^(GLOBAL|CATEGORY:(TASK_RESULTS|BILLING|SECURITY|REMINDERS|SUMMARIES|ANNOUNCEMENTS))$/),
    enabled: z.boolean(),
    effectiveEnabled: z.boolean(),
    reasonCode: z.string().max(64).nullable(),
    updatedAt: z.string().nullable(),
});
export const adminNotificationControlParamsSchema = z.object({
    key: z
        .string()
        .regex(/^(GLOBAL|CATEGORY:(TASK_RESULTS|BILLING|SECURITY|REMINDERS|SUMMARIES|ANNOUNCEMENTS))$/),
});
export const adminNotificationControlUpdateSchema = z.object({
    enabled: z.boolean(),
    confirmation: z.string().min(1).max(64),
    reason: z.enum(NOTIFICATION_OPERATOR_REASONS),
});
//# sourceMappingURL=notifications.js.map